import {RefObject, useEffect, useMemo, useRef} from "react";

import {useLeadData} from "../../application/hooks/use_lead_data";
import {AmplitudeAuthLocation} from "../../auth/tracking/track_amplitude_auth_event";
import {useAppSelector} from "../../store/hooks";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {getReservationTracker} from "../tracking/get_reservation_tracker";
import {IReservationTrackingContext} from "../tracking/track_reservation_events";
import {IReservationError} from "../types/IReservationError";
import {IReservationRequest} from "../types/IReservationRequest";
import {ReservationFlowState} from "../types/ReservationFlowState";
import {ReservationStepName} from "../types/ReservationStepName";

export interface IReservationFlowTracking {
    // a run has started from idle
    started: (request: IReservationRequest) => void;
    // a code has been sent - only the first one of a run is reported
    codeSent: (request: IReservationRequest) => void;
    codeSubmitted: (request: IReservationRequest) => void;
    // `codesSent` - codes sent before this resend
    resent: (request: IReservationRequest, codesSent: number) => void;
    // also after a reset - the reservation exists all the same
    confirmed: (request: IReservationRequest, applicationUuid: string | null) => void;
    failed: (request: IReservationRequest, error: IReservationError) => void;
    // the run has ended - the next one is reported anew
    reset: () => void;
}

// the steps whose views are reported per showing - the loader and the failure screens report none
const getViewedStep = (state: ReservationFlowState): ReservationStepName | null => {
    switch (state.status) {
        case "code_entry":
        case "confirming":
            return "code";
        case "confirmed":
            return "confirmed";
        default:
            return null;
    }
};

interface IUseReservationFlowTrackingParams {
    request: IReservationRequest | null;
    state: ReservationFlowState;
    // false while the host hides the steps - their views are reported once per showing
    isVisible: boolean;
    // omitted - the placement of the reservation button is reported
    authPlacement?: AmplitudeAuthPlacement | null;
    amplitudeLocation?: AmplitudeAuthLocation;
}

type LatestTrackingRef = RefObject<{request: IReservationRequest | null; context: IReservationTrackingContext}>;

interface IReservationFlowTrackingRefs {
    latestRef: LatestTrackingRef;
    // lead_form_start waiting for the lead data that fills in its products
    pendingLeadFormStartRef: RefObject<IReservationRequest | null>;
    hasTrackedCodeViewRef: RefObject<boolean>;
    // the steps reported while the host shows them - cleared once it hides them
    viewedStepsRef: RefObject<Set<ReservationStepName>>;
}

const createReservationFlowTracking = (refs: IReservationFlowTrackingRefs) => {
    const {latestRef, pendingLeadFormStartRef, hasTrackedCodeViewRef, viewedStepsRef} = refs;
    const getContext = () => latestRef.current.context;

    // lead_form_start waits for the lead data that fills in its products, but never comes after another event of the run
    const trackPendingLeadFormStart = (isLeadDataRequired: boolean) => {
        const pendingRequest = pendingLeadFormStartRef.current;
        const context = getContext();

        if (pendingRequest === null || (isLeadDataRequired && !context.leadData)) {
            return;
        }

        pendingLeadFormStartRef.current = null;
        getReservationTracker(pendingRequest).leadFormStart(pendingRequest, context);
    };

    const events: IReservationFlowTracking = {
        started: (startedRequest) => {
            // a funnel resumed after an expired session has reported its lead_form_start already
            if (!startedRequest.isLeadFormStartTracked) {
                pendingLeadFormStartRef.current = startedRequest;
                trackPendingLeadFormStart(true);
            }
        },
        codeSent: (sentRequest) => {
            if (hasTrackedCodeViewRef.current) {
                return;
            }

            hasTrackedCodeViewRef.current = true;
            trackPendingLeadFormStart(false);
            getReservationTracker(sentRequest).codeView(sentRequest, getContext());
        },
        codeSubmitted: (submittedRequest) => getReservationTracker(submittedRequest).codeSubmit(submittedRequest, getContext()),
        resent: (resentRequest, codesSent) => getReservationTracker(resentRequest).resend(resentRequest, codesSent, getContext()),
        confirmed: (confirmedRequest, applicationUuid) => getReservationTracker(confirmedRequest).confirmed(confirmedRequest, applicationUuid, getContext()),
        failed: (failedRequest, error) => {
            trackPendingLeadFormStart(false);
            getReservationTracker(failedRequest).error(failedRequest, error, getContext());
        },
        reset: () => {
            trackPendingLeadFormStart(false);
            hasTrackedCodeViewRef.current = false;
            viewedStepsRef.current.clear();
        }
    };

    return {events, trackLeadFormStartWithLeadData: () => trackPendingLeadFormStart(true)};
};

// Once per showing of a step: going back to the choice and forward again shows it anew. Guarded by a ref, so a repeated effect reports nothing twice
const useReservationStepViewTracking = (
    refs: Pick<IReservationFlowTrackingRefs, "latestRef" | "viewedStepsRef">,
    isVisible: boolean,
    viewedStep: ReservationStepName | null
) => {
    const {latestRef, viewedStepsRef} = refs;

    useEffect(() => {
        const viewedSteps = viewedStepsRef.current;

        if (!isVisible) {
            viewedSteps.clear();
            return;
        }

        const {request: viewedRequest, context} = latestRef.current;

        if (viewedStep === null || viewedRequest === null || viewedSteps.has(viewedStep)) {
            return;
        }

        viewedSteps.add(viewedStep);
        getReservationTracker(viewedRequest).stepView(viewedRequest, viewedStep, context);
    }, [isVisible, viewedStep, latestRef, viewedStepsRef]);
};

// Reports the flow through the tracker of the request's funnel, with the auth placement, the view type and the lead data of the moment
export const useReservationFlowTracking = (params: IUseReservationFlowTrackingParams): IReservationFlowTracking => {
    const {request, state, isVisible, authPlacement, amplitudeLocation} = params;
    const viewType = useAppSelector((rootState) => rootState.viewType.current);
    const leadData = useLeadData({vendorId: request?.vendorId ?? 0, offerId: request?.offerId, propertyId: request?.propertyId, skipFetch: request === null});

    const latestRef = useRef<LatestTrackingRef["current"]>({request, context: {authPlacement, amplitudeLocation, viewType, leadData}});
    const pendingLeadFormStartRef = useRef<IReservationRequest | null>(null);
    const hasTrackedCodeViewRef = useRef(false);
    const viewedStepsRef = useRef(new Set<ReservationStepName>());

    useEffect(() => {
        latestRef.current = {request, context: {authPlacement, amplitudeLocation, viewType, leadData}};
    });

    const tracking = useMemo(() => createReservationFlowTracking({latestRef, pendingLeadFormStartRef, hasTrackedCodeViewRef, viewedStepsRef}), []);
    const hasLeadData = leadData !== null;

    useEffect(() => {
        if (hasLeadData) {
            tracking.trackLeadFormStartWithLeadData();
        }
    }, [hasLeadData, tracking]);

    useReservationStepViewTracking({latestRef, viewedStepsRef}, isVisible, isVisible && request !== null ? getViewedStep(state) : null);

    return tracking.events;
};
import {RefObject, useEffect, useMemo, useRef} from "react";

import {useLeadData} from "../../application/hooks/use_lead_data";
import {AmplitudeAuthLocation} from "../../auth/tracking/track_amplitude_auth_event";
import {useAppSelector} from "../../store/hooks";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {getReservationTracker} from "../tracking/get_reservation_tracker";
import {IReservationTrackingContext} from "../tracking/track_reservation_events";
import {IReservationError} from "../types/IReservationError";
import {IReservationRequest} from "../types/IReservationRequest";
import {ReservationFlowState} from "../types/ReservationFlowState";
import {ReservationStepName} from "../types/ReservationStepName";

export interface IReservationFlowTracking {
    // a run has started from idle
    started: (request: IReservationRequest) => void;
    // a code has been sent - only the first one of a run is reported
    codeSent: (request: IReservationRequest) => void;
    codeSubmitted: (request: IReservationRequest) => void;
    // `codesSent` - codes sent before this resend
    resent: (request: IReservationRequest, codesSent: number) => void;
    // also after a reset - the reservation exists all the same
    confirmed: (request: IReservationRequest, applicationUuid: string | null) => void;
    failed: (request: IReservationRequest, error: IReservationError) => void;
    // the run has ended - the next one is reported anew
    reset: () => void;
}

// the steps whose views are reported per showing - the loader and the failure screens report none
const getViewedStep = (state: ReservationFlowState): ReservationStepName | null => {
    switch (state.status) {
        case "code_entry":
        case "confirming":
            return "code";
        case "confirmed":
            return "confirmed";
        default:
            return null;
    }
};

interface IUseReservationFlowTrackingParams {
    request: IReservationRequest | null;
    state: ReservationFlowState;
    // false while the host hides the steps - their views are reported once per showing
    isVisible: boolean;
    // omitted - the placement of the reservation button is reported
    authPlacement?: AmplitudeAuthPlacement | null;
    amplitudeLocation?: AmplitudeAuthLocation;
}

type LatestTrackingRef = RefObject<{request: IReservationRequest | null; context: IReservationTrackingContext}>;

interface IReservationFlowTrackingRefs {
    latestRef: LatestTrackingRef;
    // lead_form_start waiting for the lead data that fills in its products
    pendingLeadFormStartRef: RefObject<IReservationRequest | null>;
    hasTrackedCodeViewRef: RefObject<boolean>;
    // the steps reported while the host shows them - cleared once it hides them
    viewedStepsRef: RefObject<Set<ReservationStepName>>;
}

const createReservationFlowTracking = (refs: IReservationFlowTrackingRefs) => {
    const {latestRef, pendingLeadFormStartRef, hasTrackedCodeViewRef, viewedStepsRef} = refs;
    const getContext = () => latestRef.current.context;

    // lead_form_start waits for the lead data that fills in its products, but never comes after another event of the run
    const trackPendingLeadFormStart = (isLeadDataRequired: boolean) => {
        const pendingRequest = pendingLeadFormStartRef.current;
        const context = getContext();

        if (pendingRequest === null || (isLeadDataRequired && !context.leadData)) {
            return;
        }

        pendingLeadFormStartRef.current = null;
        getReservationTracker(pendingRequest).leadFormStart(pendingRequest, context);
    };

    const events: IReservationFlowTracking = {
        started: (startedRequest) => {
            // a funnel resumed after an expired session has reported its lead_form_start already
            if (!startedRequest.isLeadFormStartTracked) {
                pendingLeadFormStartRef.current = startedRequest;
                trackPendingLeadFormStart(true);
            }
        },
        codeSent: (sentRequest) => {
            if (hasTrackedCodeViewRef.current) {
                return;
            }

            hasTrackedCodeViewRef.current = true;
            trackPendingLeadFormStart(false);
            getReservationTracker(sentRequest).codeView(sentRequest, getContext());
        },
        codeSubmitted: (submittedRequest) => getReservationTracker(submittedRequest).codeSubmit(submittedRequest, getContext()),
        resent: (resentRequest, codesSent) => getReservationTracker(resentRequest).resend(resentRequest, codesSent, getContext()),
        confirmed: (confirmedRequest, applicationUuid) => getReservationTracker(confirmedRequest).confirmed(confirmedRequest, applicationUuid, getContext()),
        failed: (failedRequest, error) => {
            trackPendingLeadFormStart(false);
            getReservationTracker(failedRequest).error(failedRequest, error, getContext());
        },
        reset: () => {
            trackPendingLeadFormStart(false);
            hasTrackedCodeViewRef.current = false;
            viewedStepsRef.current.clear();
        }
    };

    return {events, trackLeadFormStartWithLeadData: () => trackPendingLeadFormStart(true)};
};

// Once per showing of a step: going back to the choice and forward again shows it anew. Guarded by a ref, so a repeated effect reports nothing twice
const useReservationStepViewTracking = (
    refs: Pick<IReservationFlowTrackingRefs, "latestRef" | "viewedStepsRef">,
    isVisible: boolean,
    viewedStep: ReservationStepName | null
) => {
    const {latestRef, viewedStepsRef} = refs;

    useEffect(() => {
        const viewedSteps = viewedStepsRef.current;

        if (!isVisible) {
            viewedSteps.clear();
            return;
        }

        const {request: viewedRequest, context} = latestRef.current;

        if (viewedStep === null || viewedRequest === null || viewedSteps.has(viewedStep)) {
            return;
        }

        viewedSteps.add(viewedStep);
        getReservationTracker(viewedRequest).stepView(viewedRequest, viewedStep, context);
    }, [isVisible, viewedStep, latestRef, viewedStepsRef]);
};

// Reports the flow through the tracker of the request's funnel, with the auth placement, the view type and the lead data of the moment
export const useReservationFlowTracking = (params: IUseReservationFlowTrackingParams): IReservationFlowTracking => {
    const {request, state, isVisible, authPlacement, amplitudeLocation} = params;
    const viewType = useAppSelector((rootState) => rootState.viewType.current);
    const leadData = useLeadData({vendorId: request?.vendorId ?? 0, offerId: request?.offerId, propertyId: request?.propertyId, skipFetch: request === null});

    const latestRef = useRef<LatestTrackingRef["current"]>({request, context: {authPlacement, amplitudeLocation, viewType, leadData}});
    const pendingLeadFormStartRef = useRef<IReservationRequest | null>(null);
    const hasTrackedCodeViewRef = useRef(false);
    const viewedStepsRef = useRef(new Set<ReservationStepName>());

    useEffect(() => {
        latestRef.current = {request, context: {authPlacement, amplitudeLocation, viewType, leadData}};
    });

    const tracking = useMemo(() => createReservationFlowTracking({latestRef, pendingLeadFormStartRef, hasTrackedCodeViewRef, viewedStepsRef}), []);
    const hasLeadData = leadData !== null;

    useEffect(() => {
        if (hasLeadData) {
            tracking.trackLeadFormStartWithLeadData();
        }
    }, [hasLeadData, tracking]);

    useReservationStepViewTracking({latestRef, viewedStepsRef}, isVisible, isVisible && request !== null ? getViewedStep(state) : null);

    return tracking.events;
};
